import React from 'react';
import '../../styles/global.css';
import styles from '../Subscribed/subscribed.module.css';
import DownloadApp from '../../components/DownloadApp';
import appIcon from '../../assets/app-icon.webp';
import { CLEVERTAP_EVENTS } from '../../analytics/trackerEvents/cleverTap/events';
import { CT_PARAMS } from '../../analytics/trackerEvents/cleverTap/parameters';
import { trackCleverTapEvent } from '../../utils/cleverTapHelper';
import { useTokenStore } from '../../storage/tokenState';
import { getVerticalTVRedirectUrl, getUTMParamsFromURL } from '../../utils/utmTracker';
import appsFlyerSmartScriptLib from '../../utils/SmartScriptAppFlyer/appsFlyerSmartScriptLib';
import { getStoredOneLinkURL, fireButtonClickEvent } from '../../utils/SmartScriptAppFlyer/appsFlyerEvents';

// Default Play Store URL (fallback)
const DEFAULT_PLAYSTORE_URL = 'https://play.google.com/store/apps/details?id=com.eight.network.android.vertical';
const DEFAULT_DEEP_LINK_URL = 'https://www.verticaltvbyzupee.com/?z=77';
const ANDROID_PACKAGE_NAME = 'com.eight.network.android.vertical';

const hasRealUTMParams = (): boolean => {
  const utmParams = getUTMParamsFromURL();
  return !!(utmParams.utm_source || utmParams.utm_campaign || utmParams.fbclid || utmParams.gclid);
};

const isAndroidDevice = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /android/i.test(navigator.userAgent || '');
};

const buildAndroidIntentUrl = (deepLinkUrl: string, fallbackUrl: string): string => {
  const stripped = deepLinkUrl.replace(/^https?:\/\//, '');
  const encodedFallback = encodeURIComponent(fallbackUrl);
  return `intent://${stripped}#Intent;scheme=https;package=${ANDROID_PACKAGE_NAME};S.browser_fallback_url=${encodedFallback};end`;
};

const AppDownload: React.FC = () => {
  const { token } = useTokenStore();
  const [appsFlyerDownloadUrl, setAppsFlyerDownloadUrl] = React.useState<string | null>(null);
  const smartScriptInitializedRef = React.useRef(false);
  const hasUTMParams = React.useRef(hasRealUTMParams());

  // Check if user came with titleId - use deep link ONLY when titleId is present
  const verticalTVUrl = getVerticalTVRedirectUrl();


  const getDeepLinkUrl = (): string => {
    return verticalTVUrl || DEFAULT_DEEP_LINK_URL;
  };

  const getPlayStoreUrl = (): string => {
    const separator = DEFAULT_PLAYSTORE_URL.includes('?') ? '&' : '?';
    return `${DEFAULT_PLAYSTORE_URL}${separator}source=trailpwawebsite`;
  };

  const getOneLinkUrl = (): string | null => {
    if (!appsFlyerDownloadUrl || !hasUTMParams.current) {
      return null;
    }
    return appsFlyerDownloadUrl;
  };

  React.useEffect(() => {
    if (smartScriptInitializedRef.current) {
      return;
    }
    smartScriptInitializedRef.current = true;

    try {
      appsFlyerSmartScriptLib();
      window.setTimeout(() => {
        const oneLinkUrl = getStoredOneLinkURL();
        if (oneLinkUrl) {
          setAppsFlyerDownloadUrl(oneLinkUrl);
        }
      }, 100);
    } catch (error) {
      // Silent fail - fallback to default Play Store URL
    }
  }, []);

  const openDeepLinkWithFallback = () => {
    const deepLinkUrl = getDeepLinkUrl();
    const oneLinkUrl = getOneLinkUrl();
    const playStoreUrl = getPlayStoreUrl();
    let didHide = false;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        didHide = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Non-Android: go directly to Play Store
    if (!isAndroidDevice()) {
      window.location.href = playStoreUrl;
      return;
    }

    // Android: use intent to allow browser fallback
    if (isAndroidDevice()) {
      const fallbackUrl = oneLinkUrl || playStoreUrl;
      window.location.href = buildAndroidIntentUrl(deepLinkUrl, fallbackUrl);
      return;
    }

    // Step 1: try deep link
    window.location.href = deepLinkUrl;

    // Step 2: if app didn't open, try OneLink (if available)
    window.setTimeout(() => {
      if (didHide) {
        return;
      }
      if (oneLinkUrl) {
        window.location.href = oneLinkUrl;
      }
    }, 800);

    // Step 3: if still not opened, fallback to Play Store
    window.setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (!didHide) {
        window.location.href = playStoreUrl;
      }
    }, 1600);
  };

  const handleDownloadClick = () => {
    const userId = token?.zupeeUserId?.toString() || '';

    // Track CleverTap event
    trackCleverTapEvent(CLEVERTAP_EVENTS.WEB_EVENT_DOWNLOAD_APP_CLICKED, {
      [CT_PARAMS.USER_ID]: userId
    });

    // Track AppsFlyer button click event
    fireButtonClickEvent({
      buttonName: 'download_app',
      page: 'appdownload',
      action: 'download'
    });
  };

  const handleRedirect = () => {
    openDeepLinkWithFallback();
  };

  return (
    <div className={styles.subscribedPage}>
      <div className={styles.backgroundGlow}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="360"
          height="621"
          viewBox="0 0 360 621"
          fill="none"
          className={styles.greenCornerSvg}
        >
          <g filter="url(#filter0_fn_531_2611)">
            <circle cx="107" cy="592" r="158" fill="#0C4615" />
          </g>
          <defs>
            <filter id="filter0_fn_531_2611" x="-485" y="0" width="1184" height="1184" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_531_2611" />
              <feTurbulence type="fractalNoise" baseFrequency="0.5 0.5" stitchTiles="stitch" numOctaves="3" result="noise" seed="1097" />
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5" />
                <feFuncG type="linear" slope="2" intercept="-0.5" />
                <feFuncB type="linear" slope="2" intercept="-0.5" />
                <feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 " />
              </feComponentTransfer>
              <feComposite operator="in" in2="effect1_foregroundBlur_531_2611" in="coloredNoise1" result="noise1Clipped" />
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.3" />
              </feComponentTransfer>
              <feMerge result="effect2_noise_531_2611">
                <feMergeNode in="effect1_foregroundBlur_531_2611" />
                <feMergeNode in="color1" />
              </feMerge>
            </filter>
          </defs>
        </svg>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="360"
          height="684"
          viewBox="0 0 360 684"
          fill="none"
          className={styles.backgroundSvg}
        >
          <g filter="url(#filter0_fn_531_2610)">
            <circle cx="360" cy="92" r="158" fill="#2CB642" />
          </g>
          <defs>
            <filter
              id="filter0_fn_531_2610"
              x="-232"
              y="-500"
              width="1184"
              height="1184"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_531_2610" />
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.5 0.5"
                stitchTiles="stitch"
                numOctaves="3"
                result="noise"
                seed="2135"
              />
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5" />
                <feFuncG type="linear" slope="2" intercept="-0.5" />
                <feFuncB type="linear" slope="2" intercept="-0.5" />
                <feFuncA
                  type="discrete"
                  tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0"
                />
              </feComponentTransfer>
              <feComposite
                operator="in"
                in2="effect1_foregroundBlur_531_2610"
                in="coloredNoise1"
                result="noise1Clipped"
              />
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.4" />
              </feComponentTransfer>
              <feMerge result="effect2_noise_531_2610">
                <feMergeNode in="effect1_foregroundBlur_531_2610" />
                <feMergeNode in="color1" />
              </feMerge>
            </filter>
          </defs>
        </svg>
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.iconContainer}>
          <div className={styles.shadowWrapper}>
            <svg
              className={styles.iconShadow}
              xmlns="http://www.w3.org/2000/svg"
              width="325"
              height="264"
              viewBox="0 0 325 264"
              fill="none"
            >
              <g filter="url(#filter0_f_531_2607)">
                <path
                  d="M34 34H291L248.682 199.887C244.162 217.603 228.206 230 209.923 230H115.077C96.7937 230 80.8376 217.603 76.3182 199.887L34 34Z"
                  fill="url(#paint0_linear_531_2607)"
                />
              </g>
              <defs>
                <filter
                  id="filter0_f_531_2607"
                  x="0"
                  y="0"
                  width="325"
                  height="264"
                  filterUnits="userSpaceOnUse"
                  colorInterpolationFilters="sRGB"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                  <feGaussianBlur stdDeviation="17" result="effect1_foregroundBlur_531_2607" />
                </filter>
                <linearGradient
                  id="paint0_linear_531_2607"
                  x1="163"
                  y1="220"
                  x2="163"
                  y2="34"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#063947" />
                  <stop offset="1" stopColor="#513606" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className={styles.smileyIcon}>
            <img
              src={appIcon}
              alt="App icon"
              className={styles.smileyImage}
            />
          </div>
        </div>

        <div className={styles.textContainer}>
          <h1 className={styles.mainHeading}>
            <span style={{ width: '100%', textAlign: 'center' }}>Exciting shows</span>
            <span style={{ width: '100%', textAlign: 'center' }}>waiting for you 🎉</span>
          </h1>
        </div>

        <div className={styles.downloadSection}>
          <div className={styles.buttonGroup}>
            <p className={styles.subHeading}>
              {verticalTVUrl ? 'Open app and explore' : 'Download the app and explore'}
            </p>
            <div className={styles.buttonWrapper}>
              <DownloadApp
                showRedirecting={false}
                showButton={true}
                redirectUrl={getPlayStoreUrl()}
                onClick={handleDownloadClick}
                onRedirect={handleRedirect}
              />
            </div>
          </div>
          <div className={styles.redirectingWrapper}>
            <DownloadApp
              showRedirecting={true}
              showButton={false}
              redirectUrl={getPlayStoreUrl()}
              onRedirect={handleRedirect}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppDownload;
